import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import { PurchaseOrderStatus } from '../enums/purchase-order-status';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { Product } from '../interfaces/product';
import { PurchaseOrder } from '../interfaces/purchase-order';
import { PurchaseOrderProduct } from '../interfaces/purchase-order-product';
import { PurchaseOrderRevisionDto } from '../interfaces/purchase-order-revision.dto';
import { PurchaseOrderDto } from '../interfaces/purchase-order.dto';
import { UpdatePurchaseOrderDto } from '../interfaces/update-purchase-order.dto';
import { api } from '../services/api';
import myToastr from '../services/toastr';
import { RootState } from './store';

export interface ProductInShoppingCart {
  product: Product;
  costPrice: number;
  retailPrice: number;
  units: number;
  discountPercentage: number | null;
}

export interface PurchaseOrderState {
  requesting: boolean;
  purchaseOrder: PurchaseOrder | null;
  organizationClientId: number;
  destinationStoreId: number | null;
  products: ProductInShoppingCart[];
  productUnitsMap: { [productId: number]: number };
  productRetailPriceMap: { [productId: number]: number };
  productDiscountPercentageMap: { [productId: number]: number | null };
  total: number;
  shippingCountry: string | null;
  shippingAddress: string | null;
  shippingZipCode: string | null;
  shippingCity: string | null;
  shippingProvince: string | null;
  sentProducts: { [id: number]: number | undefined };
}

const initialState: PurchaseOrderState = {
  requesting: false,
  purchaseOrder: null,
  organizationClientId: -1,
  destinationStoreId: -1,
  total: 0,
  products: [],
  productUnitsMap: {},
  productRetailPriceMap: {},
  productDiscountPercentageMap: {},
  shippingCountry: '',
  shippingAddress: '',
  shippingZipCode: '',
  shippingCity: '',
  shippingProvince: '',
  sentProducts: {},
};

function* updateTotal() {
  yield put(purchaseOrderSlice.actions.updateTotal());
}

export const getPurchaseOrder = createAsyncThunk('purchaseOrder/getPurchaseOrder', async (id: number, thunkApi): Promise<PurchaseOrder | null> => {
  try {
    const po: PurchaseOrder = await api.getPurchaseOrder(id);
    thunkApi.dispatch(purchaseOrderSlice.actions.setPurchaseOrder(po));
    return po;
  } catch (e: any) {
    const axiosError: AxiosError<HttpExceptionDto> = e;
    if (axiosError.response?.data) {
      const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
      myToastr.error(httpExceptionDto.message);
    }
    return null;
  }
});

export const createPurchaseOrder = createAsyncThunk('purchaseOrder/createPurchaseOrder', async (_: void, thunkApi): Promise<PurchaseOrder | null> => {
  const state: RootState = thunkApi.getState() as RootState;
  const purchaseOrderDto: PurchaseOrderDto = {
    clientOrganizationId: state.purchaseOrder.organizationClientId,
    destinationStoreId: state.purchaseOrder.destinationStoreId!,
    products: state.purchaseOrder.products.map((productInShoppingCart: ProductInShoppingCart) => ({
      productId: productInShoppingCart.product.id,
      requestedUnits: productInShoppingCart.units,
      retailPrice: productInShoppingCart.retailPrice,
      discountPercentage: productInShoppingCart.discountPercentage,
    })),
  };
  thunkApi.dispatch(purchaseOrderSlice.actions.setRequesting(true));
  let purchaseOrder: PurchaseOrder | null = null;
  try {
    purchaseOrder = await api.createPurchaseOrder(purchaseOrderDto);
    thunkApi.dispatch(purchaseOrderSlice.actions.reset());
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const axiosError: AxiosError = e as AxiosError;
      if (axiosError.response?.data) {
        const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
        myToastr.error(Array.isArray(httpExceptionDto.message) ? httpExceptionDto.message.join('\n') : httpExceptionDto.message);
      }
    }
  }
  thunkApi.dispatch(purchaseOrderSlice.actions.setRequesting(false));
  return purchaseOrder;
});

export const updatePurchaseOrder = createAsyncThunk(
  'purchaseOrder/updatePurchaseOrder',
  async (status: PurchaseOrderStatus.Draft | PurchaseOrderStatus.Generated, thunkApi): Promise<PurchaseOrder | null> => {
    const state: RootState = thunkApi.getState() as RootState;
    const updatePurchaseOrderDto: UpdatePurchaseOrderDto = {
      status,
      products: state.purchaseOrder.products.map((productInShoppingCart: ProductInShoppingCart) => ({
        productId: productInShoppingCart.product.id,
        requestedUnits: productInShoppingCart.units,
        retailPrice: productInShoppingCart.retailPrice,
        discountPercentage: productInShoppingCart.discountPercentage,
      })),
      shippingCountry: state.purchaseOrder.shippingCountry,
      shippingAddress: state.purchaseOrder.shippingAddress,
      shippingZipCode: state.purchaseOrder.shippingZipCode,
      shippingCity: state.purchaseOrder.shippingCity,
      shippingProvince: state.purchaseOrder.shippingProvince,
    };
    thunkApi.dispatch(purchaseOrderSlice.actions.setRequesting(true));
    let purchaseOrder: PurchaseOrder | null = null;
    try {
      purchaseOrder = await api.updatePurchaseOrder(state.purchaseOrder.purchaseOrder!.id, updatePurchaseOrderDto);
      thunkApi.dispatch(purchaseOrderSlice.actions.reset());
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const axiosError: AxiosError = e as AxiosError;
        if (axiosError.response?.data) {
          const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
          myToastr.error(Array.isArray(httpExceptionDto.message) ? httpExceptionDto.message.join('\n') : httpExceptionDto.message);
        }
      }
    }
    thunkApi.dispatch(purchaseOrderSlice.actions.setRequesting(false));
    return purchaseOrder;
  },
);

export const revisionPurchaseOrder = createAsyncThunk(
  'purchaseOrder/revisionPurchaseOrder',
  async (status: PurchaseOrderStatus.Generated | PurchaseOrderStatus.DeliveryNote | PurchaseOrderStatus.Invoice, thunkApi): Promise<PurchaseOrder | null> => {
    const state: RootState = thunkApi.getState() as RootState;
    thunkApi.dispatch(purchaseOrderSlice.actions.setRequesting(true));
    let purchaseOrder: PurchaseOrder | null = null;
    try {
      const purchaseOrderRevisionDto: PurchaseOrderRevisionDto = {
        status,
        products: [],
      };
      for (const id in state.purchaseOrder.sentProducts) {
        purchaseOrderRevisionDto.products.push({
          id: parseInt(id as any, 10),
          finalUnits: state.purchaseOrder.sentProducts[id] || 0,
        });
      }
      purchaseOrder = await api.purchaseOrderRevision(state.purchaseOrder.purchaseOrder!.id, purchaseOrderRevisionDto);
      thunkApi.dispatch(purchaseOrderSlice.actions.setPurchaseOrder(purchaseOrder));
      if (status === PurchaseOrderStatus.DeliveryNote) {
        myToastr.success('Albarán generado correctamente');
      } else if (status === PurchaseOrderStatus.Invoice) {
        myToastr.success('Factura generada correctamente');
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const axiosError: AxiosError = e as AxiosError;
        if (axiosError.response?.data) {
          const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
          myToastr.error(Array.isArray(httpExceptionDto.message) ? httpExceptionDto.message.join('\n') : httpExceptionDto.message);
        }
      }
    }
    thunkApi.dispatch(purchaseOrderSlice.actions.setRequesting(false));
    return purchaseOrder;
  },
);

export const purchaseOrderSlice = createSlice({
  name: 'purchaseOrder',
  initialState,
  reducers: {
    setRequesting: (state: PurchaseOrderState, payloadAction: PayloadAction<boolean>) => {
      state.requesting = payloadAction.payload;
    },
    setPurchaseOrder: (state: PurchaseOrderState, payloadAction: PayloadAction<PurchaseOrder>) => {
      state.purchaseOrder = payloadAction.payload;
      state.shippingCountry = payloadAction.payload.shippingCountry;
      state.shippingAddress = payloadAction.payload.shippingAddress;
      state.shippingZipCode = payloadAction.payload.shippingZipCode;
      state.shippingCity = payloadAction.payload.shippingCity;
      state.shippingProvince = payloadAction.payload.shippingProvince;
      state.productUnitsMap = payloadAction.payload.purchaseOrderProducts.reduce((productUnitsMap: { [productId: number]: number }, purchaseOrderProduct: PurchaseOrderProduct) => {
        productUnitsMap[purchaseOrderProduct.product.id] = purchaseOrderProduct.requestedUnits;
        return productUnitsMap;
      }, {});
      state.productRetailPriceMap = payloadAction.payload.purchaseOrderProducts.reduce((productRetailPriceMap: { [productId: number]: number }, purchaseOrderProduct: PurchaseOrderProduct) => {
        productRetailPriceMap[purchaseOrderProduct.product.id] = purchaseOrderProduct.retailPrice;
        return productRetailPriceMap;
      }, {});
      state.productDiscountPercentageMap = payloadAction.payload.purchaseOrderProducts.reduce(
        (productDiscountPercentageMap: { [productId: number]: number | null }, purchaseOrderProduct: PurchaseOrderProduct) => {
          productDiscountPercentageMap[purchaseOrderProduct.product.id] = purchaseOrderProduct.discount;
          return productDiscountPercentageMap;
        },
        {},
      );
      state.products = [];
      state.sentProducts = {};
      payloadAction.payload.purchaseOrderProducts.forEach((purchaseOrderProduct: PurchaseOrderProduct) => {
        state.products.push({
          product: purchaseOrderProduct.product,
          costPrice: purchaseOrderProduct.stock.price,
          retailPrice: purchaseOrderProduct.retailPrice,
          units: purchaseOrderProduct.requestedUnits,
          discountPercentage: purchaseOrderProduct.discount,
        });
        state.sentProducts[purchaseOrderProduct.id] = purchaseOrderProduct.finalUnits;
      });
    },
    setOrganizationClientId: (state: PurchaseOrderState, payloadAction: PayloadAction<number>) => {
      state.organizationClientId = payloadAction.payload;
    },
    setDestinationStoreId: (state: PurchaseOrderState, payloadAction: PayloadAction<number | null>) => {
      state.destinationStoreId = payloadAction.payload;
    },
    setShippingCountry: (state: PurchaseOrderState, payloadAction: PayloadAction<string>) => {
      state.shippingCountry = payloadAction.payload;
    },
    setShippingAddress: (state: PurchaseOrderState, payloadAction: PayloadAction<string>) => {
      state.shippingAddress = payloadAction.payload;
    },
    setShippingZipCode: (state: PurchaseOrderState, payloadAction: PayloadAction<string>) => {
      state.shippingZipCode = payloadAction.payload;
    },
    setShippingCity: (state: PurchaseOrderState, payloadAction: PayloadAction<string>) => {
      state.shippingCity = payloadAction.payload;
    },
    setShippingProvince: (state: PurchaseOrderState, payloadAction: PayloadAction<string>) => {
      state.shippingProvince = payloadAction.payload;
    },
    addProduct: (state: PurchaseOrderState, payloadAction: PayloadAction<{ product: Product; costPrice: number; retailPrice: number }>) => {
      const { product, costPrice, retailPrice } = payloadAction.payload;
      if (state.productUnitsMap.hasOwnProperty(product.id)) {
        return;
      }
      state.products.push({
        product,
        costPrice,
        retailPrice,
        units: 1,
        discountPercentage: null,
      });
      state.productUnitsMap[product.id] = 1;
      state.productRetailPriceMap[product.id] = retailPrice;
      state.productDiscountPercentageMap[product.id] = null;
    },
    setUnits: (state: PurchaseOrderState, payloadAction: PayloadAction<{ productId: number; units: number }>) => {
      const { units, productId } = payloadAction.payload;
      if (units === 0) {
        state.products = state.products.filter((productInShoppingCart: ProductInShoppingCart) => productInShoppingCart.product.id !== productId);
        delete state.productUnitsMap[productId];
        delete state.productDiscountPercentageMap[productId];
        delete state.productRetailPriceMap[productId];
        return;
      }
      state.products = state.products.map((productInShoppingCart: ProductInShoppingCart) => {
        if (productInShoppingCart.product.id === productId) {
          productInShoppingCart.units = units;
        }
        return productInShoppingCart;
      });
      state.productUnitsMap[productId] = units;
    },
    setRetailPrice: (state: PurchaseOrderState, payloadAction: PayloadAction<{ retailPrice: number; productId: number }>) => {
      const { retailPrice, productId } = payloadAction.payload;
      state.products = state.products.map((productInShoppingCart: ProductInShoppingCart) => {
        if (productInShoppingCart.product.id === productId) {
          productInShoppingCart.retailPrice = retailPrice;
        }
        return productInShoppingCart;
      });
      state.productRetailPriceMap[productId] = retailPrice;
    },
    setDiscountPercentage: (state: PurchaseOrderState, payloadAction: PayloadAction<{ discountPercentage: number | null; productId: number }>) => {
      const { discountPercentage, productId } = payloadAction.payload;
      state.products = state.products.map((productInShoppingCart: ProductInShoppingCart) => {
        if (productInShoppingCart.product.id === productId) {
          productInShoppingCart.discountPercentage = discountPercentage;
        }
        return productInShoppingCart;
      });
      state.productDiscountPercentageMap[productId] = discountPercentage;
    },
    addProductUnit: (state: PurchaseOrderState, payloadAction: PayloadAction<number>) => {
      const productId: number = payloadAction.payload;
      if (!state.productUnitsMap.hasOwnProperty(productId)) {
        return;
      }
      state.products = state.products.map((productInShoppingCart: ProductInShoppingCart) => {
        if (productInShoppingCart.product.id === productId) {
          productInShoppingCart.units += 1;
        }
        return productInShoppingCart;
      });
      state.productUnitsMap[productId] += 1;
    },
    removeProductUnit: (state: PurchaseOrderState, payloadAction: PayloadAction<number>) => {
      const productId: number = payloadAction.payload;
      if (!state.productUnitsMap.hasOwnProperty(productId)) {
        return;
      }
      state.products = state.products.map((productInShoppingCart: ProductInShoppingCart) => {
        if (productInShoppingCart.product.id === productId) {
          productInShoppingCart.units -= 1;
        }
        return productInShoppingCart;
      });
      state.productUnitsMap[productId] -= 1;
      if (state.productUnitsMap[productId] === 0) {
        state.products = state.products.filter((productInShoppingCart: ProductInShoppingCart) => productInShoppingCart.product.id !== productId);
        delete state.productUnitsMap[productId];
        delete state.productRetailPriceMap[productId];
        delete state.productDiscountPercentageMap[productId];
      }
    },
    removeProduct: (state: PurchaseOrderState, payloadAction: PayloadAction<number>) => {
      const productId = payloadAction.payload;
      if (!state.productUnitsMap.hasOwnProperty(productId)) {
        return;
      }
      state.products = state.products.filter((productInShoppingCart: ProductInShoppingCart) => productInShoppingCart.product.id !== productId);
      delete state.productUnitsMap[productId];
      delete state.productRetailPriceMap[productId];
      delete state.productDiscountPercentageMap[productId];
    },
    removeAllProducts: (state: PurchaseOrderState) => {
      state.products = [];
      state.productUnitsMap = {};
      state.productRetailPriceMap = {};
      state.productDiscountPercentageMap = {};
    },
    reset: () => {
      return initialState;
    },
    updateTotal: (state: PurchaseOrderState) => {
      state.total = state.products.reduce((accumulator: number, productInShoppingCart: ProductInShoppingCart) => {
        const discount = productInShoppingCart.discountPercentage ? productInShoppingCart.discountPercentage : 0;
        const discountedPrice = productInShoppingCart.retailPrice * (1 - discount / 100);
        return accumulator + discountedPrice * productInShoppingCart.units;
      }, 0);
    },
    setSentUnits: (state: PurchaseOrderState, payloadAction: PayloadAction<{ id: number; finalUnits: number | undefined }>) => {
      state.sentProducts[payloadAction.payload.id] = payloadAction.payload.finalUnits;
    },
  },
});

export const {
  addProduct,
  addProductUnit,
  removeAllProducts,
  removeProduct,
  removeProductUnit,
  reset,
  setDestinationStoreId,
  setDiscountPercentage,
  setOrganizationClientId,
  setRetailPrice,
  setSentUnits,
  setShippingAddress,
  setShippingCity,
  setShippingCountry,
  setShippingProvince,
  setShippingZipCode,
  setUnits,
} = purchaseOrderSlice.actions;

export default purchaseOrderSlice.reducer;

export const purchaseOrderSelector = (state: RootState) => state.purchaseOrder;

export const purchaseOrderSaga = function* () {
  yield takeLatest(purchaseOrderSlice.actions.setPurchaseOrder.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.addProduct.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.setUnits.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.setRetailPrice.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.setDiscountPercentage.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.addProductUnit.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.removeProductUnit.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.removeProduct.type, updateTotal);
  yield takeLatest(purchaseOrderSlice.actions.reset.type, updateTotal);
};
